import { ERROR_MESSAGES } from "./constants";
import { getDefaultConfig } from "./defaults/config.default";
import { EventManagerOptions, UserConfig } from "./types";
import { getErrorMessage } from "./utils/logger";
import { getWindow } from "./utils/windowUtils";

/**
 * Class responsible for handling the configuration settings.
 */
export class Config {
    private config: UserConfig = getDefaultConfig();

    /**
     * Replaces the current configuration with the provided partial configuration.
     * @param config - The partial configuration to replace the current configuration with.
     */
    replace(
        config: Partial<EventManagerOptions> & { channelId?: string }
    ): void {
        updateConfig(config, this.config);
    }

    /**
     * Sets a specific configuration key to the provided value.
     * @param key - The configuration key to set.
     * @param value - The value to set for the configuration key.
     */
    set<K extends keyof UserConfig>(key: K, value: UserConfig[K]): void {
        this.config[key] = value;
    }

    /**
     * Retrieves the value of a specific configuration key.
     * @param key - The configuration key to retrieve the value for.
     * @returns The value of the configuration key.
     */
    get<K extends keyof UserConfig>(key: K): UserConfig[K] {
        return this.config[key];
    }

    /**
     * Retrieves all user configurations.
     * @returns {UserConfig} The user configurations.
     */
    getAll(): UserConfig {
        return this.config;
    }

    /**
     * Resets the configuration to the default values.
     */
    reset(): void {
        this.config = getDefaultConfig();
    }
}

/**
 * Updates the configuration object with the provided partial configuration.
 * @param userInput - The partial configuration provided by the user.
 * @param config - The current configuration object.
 */
function updateConfig(
    userInput: Partial<EventManagerOptions> & { channelId?: string },
    config: UserConfig
): void {
    config.debug = userInput.debug ?? config.debug;

    if (userInput.channelId === "") {
        throw new Error(
            getErrorMessage(ERROR_MESSAGES.common.channelIdRequired)
        );
    }

    config.channelId = userInput.channelId ?? config.channelId;

    config.suppressErrors = userInput.suppressErrors ?? config.suppressErrors;

    config.targetOrigin = userInput.targetOrigin ?? config.targetOrigin;

    const currentWindow = getWindow();
    if (userInput.target) {
        config.targetWindow = userInput.target;
    } else if (currentWindow) {
        config.targetWindow = currentWindow;
    } else {
        config.targetWindow = {
            postMessage: () => {},
        } as unknown as Window;
    }
}
